import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ARCHON_TITLE_ENTRIES } from '../archon/components/layout/archonNavigation';
import { EVAL_TITLE_ENTRIES } from '../eval/components/layout/evalNavigation';
import { FORGE_TITLE_ENTRIES } from '../forge/components/layout/forgeNavigation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { OOS_TITLE_ENTRIES } from '../oos/components/layout/oosNavigation';
import { CONTROL_TITLE_ENTRIES } from './controlTitles';
import { TitleEntry } from './titleEntry';

/**
 * The tab reads app first, then page, then whatever identifies the row: a tab
 * strip with several Focal pages open sorts by workspace at a glance, and the
 * page name survives the truncation that follows it.
 */
export const TITLE_SEPARATOR = ' | ';

export type Workspace = 'Control' | 'Forge' | 'Eval' | 'Archon' | 'OOS';

/**
 * The app is a one-letter tag rather than its name: a pinned tab shows only the
 * first few characters, and `[F]` leaves all of them to the page name.
 */
const WORKSPACE_TAGS: Record<Workspace, string> = {
  Control: 'C',
  Forge: 'F',
  Eval: 'E',
  Archon: 'A',
  OOS: 'O',
};

const PRODUCTION_ORIGIN = 'https://control.focal.systems';
const STAGING_ORIGIN_PATTERN = /^https?:\/\/[^/]*\bstaging\./;

/**
 * Production carries no marker — it is where a tab is expected to be — so only
 * the environments worth noticing before acting are named.
 *
 * Production is the recognised case rather than the default: an origin nobody
 * anticipated (a dev server reached over the LAN for the mobile pass, a preview
 * deploy) is marked as not-production rather than silently passed off as it.
 * An empty origin is not an origin at all and gets no tag.
 */
export function environmentSuffix(origin: string): string {
  if (!origin || origin === PRODUCTION_ORIGIN) return '';
  if (STAGING_ORIGIN_PATTERN.test(origin)) return '-stg';
  return '-local';
}

function currentOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

export function workspaceTag(workspace: Workspace, origin: string = currentOrigin()): string {
  return `[${WORKSPACE_TAGS[workspace]}${environmentSuffix(origin)}]`;
}

interface WorkspaceTitles {
  workspace: Workspace;
  /** Path prefix that puts a route in this workspace. Control owns everything else. */
  prefix: string;
  entries: TitleEntry[];
}

const WORKSPACES: WorkspaceTitles[] = [
  { workspace: 'Forge', prefix: '/forge', entries: FORGE_TITLE_ENTRIES },
  { workspace: 'Eval', prefix: '/eval', entries: EVAL_TITLE_ENTRIES },
  { workspace: 'Archon', prefix: '/archon', entries: ARCHON_TITLE_ENTRIES },
  { workspace: 'OOS', prefix: '/oos', entries: OOS_TITLE_ENTRIES },
];

const DEFAULT_WORKSPACE: Workspace = 'Control';

function workspaceTitlesFor(pathname: string): WorkspaceTitles | null {
  return (
    WORKSPACES.find(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)) ??
    null
  );
}

export function workspaceForPath(pathname: string): Workspace {
  return workspaceTitlesFor(pathname)?.workspace ?? DEFAULT_WORKSPACE;
}

function entriesForPath(pathname: string): TitleEntry[] {
  return workspaceTitlesFor(pathname)?.entries ?? CONTROL_TITLE_ENTRIES;
}

/**
 * Two entries can match one path — `/stores/:storeName/pog-reset` and
 * `/stores/:storeName/:deviceName` both answer to a store's reset page — so the
 * more literal pattern wins rather than whichever happens to be declared first.
 */
function specificity(path: string): number {
  const segments = path.split('/').filter(Boolean);
  const dynamic = segments.filter((segment) => segment.startsWith(':')).length;
  return (segments.length - dynamic) * 10 - dynamic;
}

interface EntryMatch {
  entry: TitleEntry;
  params: Record<string, string | undefined>;
}

function findEntry(entries: TitleEntry[], pathname: string): EntryMatch | null {
  const exact = entries
    .map((entry) => ({ entry, match: matchPath({ path: entry.path, end: true }, pathname) }))
    .sort((left, right) => specificity(right.entry.path) - specificity(left.entry.path))
    .find((candidate) => candidate.match !== null);
  if (exact?.match) return { entry: exact.entry, params: exact.match.params };

  // A route with no entry of its own — an unnamed deep link, a legacy alias —
  // takes the name of the closest ancestor that has one, rather than dropping
  // all the way back to the bare workspace name.
  const ancestor = entries
    .map((entry) => ({ entry, match: matchPath({ path: entry.path, end: false }, pathname) }))
    .filter((candidate) => candidate.match !== null)
    .sort(
      (left, right) => (right.match?.pathname.length ?? 0) - (left.match?.pathname.length ?? 0)
    )[0];
  return ancestor?.match ? { entry: ancestor.entry, params: ancestor.match.params } : null;
}

/**
 * An id is shown by its tail, the part that differs between two of them.
 * Anything already short enough to read whole is left alone.
 */
export function shortenId(value: string): string {
  return value.length > 6 ? `…${value.slice(-4)}` : value;
}

export function formatDocumentTitle(
  workspace: Workspace,
  tokens: (string | null | undefined)[]
): string {
  const named = tokens.filter((token): token is string => Boolean(token));
  // A route with nothing but its app to show falls back to the app's own name,
  // so the tab is never just a bracketed letter.
  return `${workspaceTag(workspace)} ${(named.length ? named : [workspace]).join(TITLE_SEPARATOR)}`;
}

/** The tab title for a path, from the URL alone. */
export function resolveDocumentTitle(pathname: string): string {
  const workspace = workspaceForPath(pathname);
  const match = findEntry(entriesForPath(pathname), pathname);
  if (!match) return formatDocumentTitle(workspace, []);

  const params = (match.entry.params ?? []).map((param) => {
    const value = match.params[param.name];
    if (!value) return null;
    return param.opaque ? shortenId(value) : value;
  });
  return formatDocumentTitle(workspace, [match.entry.label, ...params]);
}

/**
 * The tab title once the page has loaded what it is showing. The page name
 * stays and the URL-derived tail is replaced, so `[C] Store |
 * lidl_fulham_uk` becomes `[C] Store | Lidl Fulham`.
 */
export function resolveEntityTitle(
  pathname: string,
  entity: (string | null | undefined)[]
): string {
  const workspace = workspaceForPath(pathname);
  const match = findEntry(entriesForPath(pathname), pathname);
  return formatDocumentTitle(workspace, [match?.entry.label, ...entity]);
}

/**
 * The one writer of `document.title`, and the channel a page uses to refine it.
 *
 * It wraps the router root rather than sitting in the four workspace layouts so
 * that redirects, the error page and any route outside a shell are named too,
 * and so that no two writers can disagree.
 */
const EntityTitleContext = React.createContext<((tokens: string[] | null) => void) | null>(null);

interface EntityTitle {
  /** The path the page was showing when it named itself. */
  pathname: string;
  tokens: string[];
}

export const DocumentTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const [entity, setEntity] = useState<EntityTitle | null>(null);

  // A page names itself from an effect, which runs before this component sees
  // the new location, so the entity carries the path it belongs to and a
  // leftover from the page just left is ignored rather than raced away.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const publish = useCallback((tokens: string[] | null) => {
    setEntity(tokens ? { pathname: pathnameRef.current, tokens } : null);
  }, []);

  const title = useMemo(() => {
    const tokens = entity?.pathname === pathname ? entity.tokens : null;
    return tokens ? resolveEntityTitle(pathname, tokens) : resolveDocumentTitle(pathname);
  }, [entity, pathname]);
  useDocumentTitle(title);

  return <EntityTitleContext.Provider value={publish}>{children}</EntityTitleContext.Provider>;
};

/**
 * Stage two, for a page that knows more than the URL does: the loaded name of
 * the thing on screen replaces the identifier the tab started with, so
 * `[C] Store | lidl_fulham_uk` becomes `[C] Store | Lidl Fulham`.
 * Pass nothing, or only empty values, while the page is still loading and the
 * URL-derived title stands.
 *
 * Outside the provider — a page rendered on its own in a test — this does
 * nothing.
 */
export function useEntityTitle(...entity: (string | null | undefined)[]): void {
  const publish = useContext(EntityTitleContext);
  const tokens = entity.filter((token): token is string => Boolean(token));
  // The array is rebuilt on every render, so the effect keys off its content.
  const key = JSON.stringify(tokens);

  useEffect(() => {
    if (!publish || !tokens.length) return undefined;
    publish(tokens);
    return () => publish(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, key]);
}
